import { idadeEmMeses } from '@equiview/domain';
import { traitsV1, type Trait } from '@equiview/protocol';
import { uuidv7 } from '@equiview/sync';
import type {
  AnaliseDeMidia,
  Animal,
  Avaliacao,
  FonteDeDados,
  Haras,
  Manejo,
  MedicaoPorFoto,
  Medida,
  Midia,
  Observacao,
  Sexo,
  Usuario,
  Visita,
} from './fonte.js';
import type { CavaloReal } from './real/tipos.js';
import { podeAnalisar } from './fonte.js';
import { lerPerfil, type Perfil } from '../lib/perfil.js';

/**
 * Dados FICTICIOS para montar as telas antes de haver coleta real.
 *
 * Deterministico (semente fixa) e relativo a hoje: as idades sao definidas em
 * meses, entao os potros continuam nas faixas certas em qualquer dia em que o
 * app for aberto. Nenhum animal, haras ou registro aqui existe.
 *
 * Casos que existem de proposito, para testar as regras de classe:
 *   - potros nas tres faixas (0-12, 12-24, 24-36), inclusive um castrado;
 *   - Relampago (33 meses) e Graca (34 meses) ja tem filho: NAO sao potros;
 *   - avaliacoes antigas de nivel 1, com menos medidas que as de nivel 2.
 */

const HARAS: Haras = {
  id: 'h-demo',
  name: 'Haras Recanto Azul',
  city: 'São João del-Rei',
  uf: 'MG',
  abccmm_code: null,
  owner_contact: null,
  data_sharing_level: 'privado',
  notes: null,
};

/**
 * Quem usa a demo sai do "Ver como": o avaliador ve o campo de analise e o
 * relatorio de repetibilidade; o dono do haras so consulta.
 */
const usuarioDaDemo = (perfil: Perfil): Usuario =>
  perfil === 'haras'
    ? { id: 'p-demo-dono', nome: 'Proprietário do haras (demo)', papel: 'haras' }
    : { id: 'p-demo', nome: 'Avaliador (demo)', papel: 'avaliador' };
const EQUIPE = { id: 'p-equipe', nome: 'Equipe Equiview (demo)' };

interface Definicao {
  chave: string;
  nome: string;
  sexo: Sexo;
  meses: number;
  pai?: string;
  mae?: string;
}

// Pais antes dos filhos: o porte do filho herda do porte dos pais.
const DEFINICOES: Definicao[] = [
  { chave: 'g1', nome: 'Imperador do Recanto Azul', sexo: 'macho_inteiro', meses: 150 },
  { chave: 'g2', nome: 'Talismã do Recanto Azul', sexo: 'macho_inteiro', meses: 118 },
  { chave: 'g3', nome: 'Fidalgo da Boa Esperança', sexo: 'macho_inteiro', meses: 96 },
  { chave: 'g4', nome: 'Castelo do Recanto Azul', sexo: 'macho_inteiro', meses: 70 },
  { chave: 'e1', nome: 'Estrela do Recanto Azul', sexo: 'femea', meses: 160 },
  { chave: 'e2', nome: 'Joia do Recanto Azul', sexo: 'femea', meses: 132 },
  { chave: 'e3', nome: 'Aurora da Boa Esperança', sexo: 'femea', meses: 110 },
  { chave: 'e4', nome: 'Brisa do Recanto Azul', sexo: 'femea', meses: 90 },
  { chave: 'e5', nome: 'Cigana do Recanto Azul', sexo: 'femea', meses: 76 },
  { chave: 'e6', nome: 'Harmonia do Recanto Azul', sexo: 'femea', meses: 60 },
  { chave: 'e7', nome: 'Serena do Recanto Azul', sexo: 'femea', meses: 52 },
  { chave: 'c1', nome: 'Bravo do Recanto Azul', sexo: 'castrado', meses: 120 },
  { chave: 'c2', nome: 'Soberano do Recanto Azul', sexo: 'castrado', meses: 44 },
  { chave: 'j1', nome: 'Destemido do Recanto Azul', sexo: 'macho_inteiro', meses: 42, pai: 'g1', mae: 'e1' },
  { chave: 'j2', nome: 'Bela Vista do Recanto Azul', sexo: 'femea', meses: 40, pai: 'g2', mae: 'e2' },
  { chave: 's1', nome: 'Relâmpago do Recanto Azul', sexo: 'macho_inteiro', meses: 33, pai: 'g1', mae: 'e3' },
  { chave: 's2', nome: 'Graça do Recanto Azul', sexo: 'femea', meses: 34, pai: 'g2', mae: 'e4' },
  { chave: 'p1', nome: 'Trovão do Recanto Azul', sexo: 'macho_inteiro', meses: 30, pai: 'g2', mae: 'e1' },
  { chave: 'p2', nome: 'Lua do Recanto Azul', sexo: 'femea', meses: 26, pai: 'g3', mae: 'e5' },
  { chave: 'p5', nome: 'Faísca do Recanto Azul', sexo: 'castrado', meses: 25, pai: 'g3', mae: 'e7' },
  { chave: 'p3', nome: 'Pérola do Recanto Azul', sexo: 'femea', meses: 20, pai: 'g1', mae: 'e6' },
  { chave: 'p4', nome: 'Duque do Recanto Azul', sexo: 'macho_inteiro', meses: 15, pai: 'g4', mae: 'e2' },
  { chave: 'p6', nome: 'Cometa do Recanto Azul', sexo: 'macho_inteiro', meses: 9, pai: 'g4', mae: 'e3' },
  { chave: 'p7', nome: 'Ventania do Recanto Azul', sexo: 'femea', meses: 5, pai: 'g1', mae: 'e4' },
  { chave: 'p8', nome: 'Orvalho do Recanto Azul', sexo: 'macho_inteiro', meses: 2, pai: 's1', mae: 'e5' },
  { chave: 'p9', nome: 'Garoa do Recanto Azul', sexo: 'femea', meses: 1, pai: 'g2', mae: 's2' },
];

const PELAGENS = ['Castanho', 'Tordilho', 'Alazão', 'Preto', 'Baio', 'Pampa de castanho', 'Rosilho'];

// --- utilitarios ------------------------------------------------------------

function mulberry32(semente: number) {
  let a = semente;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const aleatorio = mulberry32(20260927);
const normal = () => {
  const u = Math.max(aleatorio(), 1e-9);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * aleatorio());
};
const limitar = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const arredondar = (v: number, casas: number) => Number(v.toFixed(casas));

const iso = (d: Date) => d.toISOString().slice(0, 10);
function somarMeses(base: string, meses: number, dias = 0): string {
  const d = new Date(`${base}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + meses);
  d.setUTCDate(d.getUTCDate() + dias);
  return iso(d);
}

// --- geracao ------------------------------------------------------------------

function gerar() {
  const hoje = iso(new Date());

  // Visitas do haras: a mais antiga foi de nivel 1 (censo), as outras completas.
  const visitas = [
    { id: 'vis-1', data: somarMeses(hoje, -40), nivel: 2 as const },
    { id: 'vis-2', data: somarMeses(hoje, -26), nivel: 1 as const },
    { id: 'vis-3', data: somarMeses(hoje, -13), nivel: 2 as const },
    { id: 'vis-4', data: somarMeses(hoje, 0, -20), nivel: 2 as const },
  ];

  const porChave = new Map<string, Animal>();
  const porte = new Map<string, number>();
  const animais: Animal[] = [];

  DEFINICOES.forEach((d, i) => {
    const pais = [d.pai, d.mae].filter((c): c is string => !!c).map((c) => porte.get(c) ?? 0);
    const herdado = pais.length ? pais.reduce((a, b) => a + b, 0) / pais.length : 0;
    porte.set(d.chave, 0.6 * herdado + 0.8 * normal());

    const a: Animal = {
      id: `a-${d.chave}`,
      abccmm_registry: `MM-${(104217 + i * 377).toString()}`,
      name: d.nome,
      sex: d.sexo,
      // Poucos dias a mais nao mudam os meses completos, entao a faixa se mantem.
      birth_date: somarMeses(hoje, -d.meses, -Math.floor(aleatorio() * 20)),
      birth_date_estimated: false,
      stud_book: null,
      microchip: null,
      notes: null,
      coat_color: PELAGENS[Math.floor(aleatorio() * PELAGENS.length)] ?? null,
      marcha_type_declared: aleatorio() < 0.55 ? 'batida' : 'picada',
      owner_farm_id: HARAS.id,
      breeder_farm_id: HARAS.id,
      sire_id: d.pai ? `a-${d.pai}` : null,
      dam_id: d.mae ? `a-${d.mae}` : null,
    };
    porChave.set(d.chave, a);
    animais.push(a);
  });

  const avaliacoes: Avaliacao[] = [];
  const medidas: Medida[] = [];
  const midias: Midia[] = [];

  for (const a of animais) {
    const sz = porte.get(a.id.slice(2)) ?? 0;
    // Tendencia individual de cada trait: o mesmo animal mantem o seu "jeito".
    const tendencia = new Map<string, number>(traitsV1.map((t) => [t.code, normal()]));
    const aprumoBase = new Map<string, number>(traitsV1.map((t) => [t.code, sortearEscore()]));

    const datas = visitas.filter((v) => v.data >= a.birth_date);
    const escolhidas = datas.filter((v, i) => i === datas.length - 1 || aleatorio() < 0.75).slice(-3);

    escolhidas.forEach((v, n) => {
      const ultima = n === escolhidas.length - 1;
      const av: Avaliacao = {
        id: `av-${a.id.slice(2)}-${n + 1}`,
        animal_id: a.id,
        visit_id: v.id,
        visit_date: v.data,
        protocol_level: v.nivel,
        status: ultima ? 'fechada' : 'validada',
        started_at: null,
        ended_at: null,
        sampled_for_double_measure: false,
      };
      avaliacoes.push(av);

      const meses = idadeEmMeses(a.birth_date, v.data);
      const valores = medirFicha(meses, sz, tendencia, aprumoBase, v.nivel);
      for (const m of valores) medidas.push({ ...m, assessment_id: av.id });

      if (ultima && aleatorio() < 0.75) midias.push(...midiasDaFicha(av.id, meses));
    });
  }

  const listaDeVisitas: Visita[] = visitas.map((v, i) => ({
    id: v.id,
    stud_farm_id: HARAS.id,
    visit_number: i + 1,
    visit_date: v.data,
    protocol_version_id: 'protocolo-demo',
    closed_at: `${v.data}T18:00:00.000Z`,
    purpose: 'rotina',
    weather: null,
    temp_c: null,
    notes: null,
  }));

  // Toda medida da rotina e a 1a rodada, do mesmo avaliador.
  const observacoes: Observacao[] = medidas.map((m) => ({
    assessment_id: m.assessment_id,
    avaliador: 'demo-1',
    rodada: 1,
    metodo: 'campo',
    trait_code: m.trait_code,
    side: m.side,
    value_num: m.value_num,
    not_applicable: m.not_applicable,
  }));

  // Dupla medicao: ~12% das fichas com uma 2a rodada de outro avaliador, com o
  // ruido tipico de cada instrumento (angulo e escore sao os menos repetiveis).
  const ruidoDe = (t: Trait) => (t.unit === 'graus' ? 1.8 : t.unit === 'escore' ? 0 : t.code === 'PE_TOR' ? 1.2 : 0.45);
  for (const av of avaliacoes) {
    if (aleatorio() >= 0.12) continue;
    av.sampled_for_double_measure = true;
    for (const m of medidas.filter((x) => x.assessment_id === av.id && x.value_num != null)) {
      const t = traitPorCodigo(m.trait_code);
      if (!t) continue;
      const trocaEscore = t.unit === 'escore' && aleatorio() < 0.15 ? (aleatorio() < 0.5 ? -1 : 1) : 0;
      observacoes.push({
        ...observacoes.find((o) => o.assessment_id === m.assessment_id && o.trait_code === m.trait_code && o.side === m.side)!,
        avaliador: 'demo-2',
        rodada: 2,
        value_num: arredondar(m.value_num! + normal() * ruidoDe(t) + trocaEscore, t.decimals),
      });
    }
  }

  // Fotogrametria: metade das fichas completas tem os angulos medidos tambem na
  // foto, com o vies tipico de um metodo novo (+1,5 grau) e mais espalhamento.
  for (const av of avaliacoes.filter((a) => a.protocol_level >= 2)) {
    if (aleatorio() >= 0.5) continue;
    for (const m of medidas.filter((x) => x.assessment_id === av.id && x.value_num != null)) {
      const t = traitPorCodigo(m.trait_code);
      if (t?.kind !== 'angulo') continue;
      observacoes.push({
        assessment_id: av.id,
        avaliador: 'demo-foto',
        rodada: 1,
        metodo: 'foto',
        trait_code: m.trait_code,
        side: 'NA',
        value_num: arredondar(m.value_num! + 1.5 + normal() * 2, 0),
        not_applicable: false,
      });
    }
  }

  // Estudo da Fase 0: 6 adultos, 3 avaliadores x 2 rodadas nas medidas do censo.
  // O perimetro toracico sai de proposito pouco repetivel: o relatorio tem de
  // mostrar uma medida que nao entra no protocolo.
  const dataEstudo = somarMeses(hoje, 0, -5);
  listaDeVisitas.unshift({
    id: 'vis-estudo',
    stud_farm_id: HARAS.id,
    visit_number: listaDeVisitas.length + 1,
    visit_date: dataEstudo,
    protocol_version_id: 'protocolo-demo',
    closed_at: `${dataEstudo}T18:00:00.000Z`,
    purpose: 'estudo_repetibilidade',
    weather: 'sol',
    temp_c: null,
    notes: 'Estudo de repetibilidade da Fase 0 (dados ficticios).',
  });
  const vies: Record<string, number> = { 'demo-1': 0, 'demo-2': 0.4, 'demo-3': -0.3 };
  const ruidoEstudo: Record<string, number> = { A_CER: 0.35, A_GAR: 0.4, PE_CAN: 0.25, PE_TOR: 9 };
  animais
    .filter((a) => idadeEmMeses(a.birth_date, dataEstudo) >= 48)
    .slice(0, 6)
    .forEach((a, i) => {
      const ficha: Avaliacao = {
        id: `av-estudo-${i + 1}`,
        animal_id: a.id,
        visit_id: 'vis-estudo',
        visit_date: dataEstudo,
        protocol_level: 1,
        status: 'fechada',
        started_at: null,
        ended_at: null,
        sampled_for_double_measure: false,
      };
      avaliacoes.push(ficha);
      for (const code of Object.keys(ruidoEstudo)) {
        const t = traitPorCodigo(code)!;
        const base =
          [...medidas].reverse().find((m) => m.trait_code === code && avaliacoes.some((x) => x.id === m.assessment_id && x.animal_id === a.id))
            ?.value_num ?? (t.minPlausible + t.maxPlausible) / 2;
        for (const avaliador of Object.keys(vies)) {
          for (const rodada of [1, 2]) {
            const valor = arredondar(base + vies[avaliador]! + normal() * ruidoEstudo[code]!, t.decimals);
            observacoes.push({
              assessment_id: ficha.id, avaliador, rodada, metodo: 'campo',
              trait_code: code, side: 'NA', value_num: valor, not_applicable: false,
            });
            if (avaliador === 'demo-1' && rodada === 1) {
              medidas.push({ assessment_id: ficha.id, trait_code: code, side: 'NA', value_num: valor, not_applicable: false });
            }
          }
        }
      }
    });

  return {
    animais,
    avaliacoes,
    medidas,
    midias,
    visitas: listaDeVisitas,
    observacoes,
    analises: analisesIniciais(midias, avaliacoes),
  };
}

const traitPorCodigo = (code: string) => traitsV1.find((t) => t.code === code);

/** Algumas midias ja chegam analisadas, uma delas com historico, para a tela ter o que mostrar. */
function analisesIniciais(midias: Midia[], avaliacoes: Avaliacao[]): AnaliseDeMidia[] {
  const dataDa = (m: Midia) => avaliacoes.find((a) => a.id === m.assessment_id)?.visit_date ?? '2026-01-01';
  const nova = (m: Midia, body: string, dias: number, supersedes: string | null = null): AnaliseDeMidia => ({
    id: `an-${m.id}-${dias}`,
    media_id: m.id,
    author_id: EQUIPE.id,
    author_name: EQUIPE.nome,
    body,
    supersedes,
    created_at: `${somarMeses(dataDa(m), 0, dias)}T14:00:00.000Z`,
    deleted_at: null,
  });

  const r: AnaliseDeMidia[] = [];
  const videos = midias.filter((m) => m.kind === 'video' && m.gait === 'marcha_media' && m.view === 'frente');
  for (const m of videos.slice(0, 3)) {
    const v1 = nova(m, 'Marcha regular, com boa elevação dos anteriores. Apoio simétrico.', 1);
    const v2 = nova(
      m,
      'Marcha regular, com boa elevação dos anteriores. Leve desvio do anterior direito para fora na fase de ' +
        'suspensão — reavaliar na próxima visita com a ferradura nova.',
      3,
      v1.id,
    );
    r.push(v1, v2);
  }
  const fotos = midias.filter((m) => m.kind === 'foto');
  for (const m of fotos.slice(0, 4)) {
    r.push(nova(m, 'Foto de perfil dentro do padrão: animal em estação, apoio nos quatro membros, escala visível.', 1));
  }
  return r;
}

const CHAVE_ANALISES = 'equiview.demo.analises';

/** O que o usuario salvou na demo sobrevive a um recarregamento. So na demo. */
function lerAnalisesSalvas(): AnaliseDeMidia[] {
  try {
    const bruto = localStorage.getItem(CHAVE_ANALISES);
    return bruto ? (JSON.parse(bruto) as AnaliseDeMidia[]) : [];
  } catch {
    return [];
  }
}

function gravarAnalisesSalvas(analises: AnaliseDeMidia[]) {
  try {
    localStorage.setItem(CHAVE_ANALISES, JSON.stringify(analises));
  } catch {
    // Armazenamento bloqueado: a analise vale ate recarregar a pagina.
  }
}

function sortearEscore(): number {
  const r = aleatorio();
  const sinal = aleatorio() < 0.5 ? -1 : 1;
  if (r < 0.6) return 0;
  if (r < 0.95) return sinal;
  return 2 * sinal;
}

/** Fracao do tamanho adulto atingida com `meses` de idade. */
const crescimento = (meses: number, ritmo: number, inicial: number) =>
  1 - (1 - inicial) * Math.exp(-meses / ritmo);

function valorAdulto(t: Trait, sz: number, tend: number) {
  const pos = limitar(0.5 + 0.13 * sz + 0.07 * tend, 0.1, 0.9);
  return t.minPlausible + (t.maxPlausible - t.minPlausible) * pos;
}

function medirFicha(
  meses: number,
  sz: number,
  tendencia: Map<string, number>,
  aprumoBase: Map<string, number>,
  nivel: 1 | 2 | 3,
): Omit<Medida, 'assessment_id'>[] {
  const r: Omit<Medida, 'assessment_id'>[] = [];
  const bruto = new Map<string, number>();
  const ruido = (v: number, pct: number) => v * (1 + pct * normal());

  for (const t of traitsV1) {
    if (t.levelMin > nivel) continue;
    const tend = tendencia.get(t.code) ?? 0;
    let v: number;
    if (t.kind === 'linear') v = ruido(valorAdulto(t, sz, tend) * crescimento(meses, 9, 0.6), 0.004);
    else if (t.kind === 'perimetro') v = ruido(valorAdulto(t, sz, tend) * crescimento(meses, 11, 0.5), 0.006);
    else if (t.kind === 'angulo') v = valorAdulto(t, 0, tend) + normal();
    else {
      const base = aprumoBase.get(t.code) ?? 0;
      const lados: Array<'E' | 'D' | 'NA'> = t.sided ? ['E', 'D'] : ['NA'];
      for (const side of lados) {
        const muda = aleatorio() < 0.15 ? (aleatorio() < 0.5 ? -1 : 1) : 0;
        r.push({ trait_code: t.code, side, value_num: limitar(base + muda, -2, 2), not_applicable: false });
      }
      continue;
    }
    bruto.set(t.code, v);
  }

  // Coerencia geometrica: garupa perto da cernelha, profundidade + vazio = cernelha.
  const cer = bruto.get('A_CER');
  if (cer != null) {
    if (bruto.has('A_GAR')) bruto.set('A_GAR', cer * (1.006 + 0.008 * normal()));
    if (bruto.has('P_TOR')) {
      const ptor = cer * (0.46 + 0.015 * normal());
      bruto.set('P_TOR', ptor);
      if (bruto.has('A_SUB')) bruto.set('A_SUB', cer - ptor + 0.4 * normal());
    }
  }

  for (const [code, v] of bruto) {
    const t = traitsV1.find((x) => x.code === code);
    r.push({ trait_code: code, side: 'NA', value_num: arredondar(v, t?.decimals ?? 1), not_applicable: false });
  }
  return r;
}

function midiasDaFicha(avaliacaoId: string, meses: number): Midia[] {
  const andamentos = meses < 30 ? (['passo'] as const) : (['passo', 'marcha_media', 'marcha_longa'] as const);
  const r: Midia[] = [
    { id: `${avaliacaoId}-foto`, assessment_id: avaliacaoId, kind: 'foto', view: 'perfil_esquerdo', gait: null, storage_path: null, pendente: false, marker_color: null, camera_distance_m: null },
  ];
  for (const gait of andamentos) {
    for (const view of ['frente', 'tras'] as const) {
      r.push({ id: `${avaliacaoId}-${view}-${gait}`, assessment_id: avaliacaoId, kind: 'video', view, gait, storage_path: null, pendente: false, marker_color: null, camera_distance_m: null });
    }
  }
  return r;
}

// --- cavalo real -------------------------------------------------------------

type DadosDaDemo = ReturnType<typeof gerar>;

/**
 * Poe o cavalo de verdade (build da apresentacao) no haras ficticio, DEPOIS de
 * gerar os ficticios e sem usar o sorteio: os inventados ficam iguais. Ele tem
 * uma visita propria. Nas observacoes (repetibilidade, exportacao no formato
 * longo) entra so o que foi medido ou lido na foto — estimativa nao e observacao.
 */
function juntarCavaloReal(dados: DadosDaDemo, real: CavaloReal): DadosDaDemo {
  const visita: Visita = {
    ...real.visita,
    stud_farm_id: HARAS.id,
    visit_number: Math.max(...dados.visitas.map((v) => v.visit_number)) + 1,
  };
  const animal: Animal = { ...real.animal, owner_farm_id: HARAS.id };
  const medidas = real.medidas.map((m) => ({ ...m }));
  const observacoes: Observacao[] = medidas
    .filter((m) => m.origem !== 'estimado')
    .map((m) => ({
      assessment_id: m.assessment_id,
      avaliador: m.origem === 'foto' ? 'equipe-foto' : 'rafael',
      rodada: 1,
      metodo: m.origem === 'foto' ? 'foto' : 'campo',
      trait_code: m.trait_code,
      side: m.side,
      value_num: m.value_num,
      not_applicable: m.not_applicable,
    }));
  return {
    ...dados,
    animais: [...dados.animais, animal],
    avaliacoes: [...dados.avaliacoes, { ...real.avaliacao }],
    medidas: [...dados.medidas, ...medidas],
    midias: [...dados.midias, ...real.midias.map(({ url: _url, ...m }) => m)],
    visitas: [visita, ...dados.visitas],
    observacoes: [...dados.observacoes, ...observacoes],
    analises: [...dados.analises, ...real.analises.map((a) => ({ ...a }))],
  };
}

// --- fonte ------------------------------------------------------------------

/**
 * Fotos de exemplo dos dados ficticios: Mangalarga Marchador de verdade, com
 * licenca livre, do Wikimedia Commons (creditos em public/demo/cavalos/
 * creditos.json e na tela /creditos-fotos). Cada animal fica sempre com a
 * mesma foto, escolhida por um hash estavel do id.
 */
export const FOTOS_DE_EXEMPLO = 7;
/** Fotos por pelagem, para a foto nao desmentir a ficha (tordilho com foto de tordilho). */
const FOTOS_POR_PELAGEM: Array<[RegExp, number[]]> = [
  [/tordilh|rosilh/i, [1, 3]],
  [/pampa/i, [7]],
  [/alaz/i, [5, 2]],
  [/castanh/i, [2, 6]],
  [/preto/i, [6]],
  [/baio|lobuno/i, [4]],
];
export function fotoDeExemplo(animalId: string, pelagem?: string | null): string {
  let h = 0;
  for (const c of animalId) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const lista = FOTOS_POR_PELAGEM.find(([re]) => pelagem && re.test(pelagem))?.[1];
  const n = lista ? lista[h % lista.length]! : (h % FOTOS_DE_EXEMPLO) + 1;
  return `/demo/cavalos/${String(n).padStart(2, '0')}.jpg`;
}

/** `real`: o cavalo de verdade da build da apresentacao (VITE_CAVALO_REAL), ou nada. */
export function criarFonteDemo(real: CavaloReal | null = null): FonteDeDados {
  const gerados = gerar();
  const dados = real ? juntarCavaloReal(gerados, real) : gerados;
  const urlReal = new Map(real?.midias.map((m) => [m.id, m.url]) ?? []);
  const manejoReal: Record<string, Manejo> = real ? { [real.avaliacao.id]: real.manejo } : {};
  const semMedicao: MedicaoPorFoto = { midiaId: null, pontos: {}, origem: {}, angulos: {}, autor: null };
  const salvas = lerAnalisesSalvas();
  const copia = <T>(v: T): Promise<T> => Promise.resolve(structuredClone(v));
  const todasAnalises = () => [...dados.analises, ...salvas];
  const usuario = usuarioDaDemo(lerPerfil());

  return {
    ficticia: true,
    haras: () => copia(HARAS),
    listarHaras: () => copia([HARAS]),
    listarAnimais: () => copia(dados.animais),
    animal: (id) => copia(dados.animais.find((a) => a.id === id) ?? null),
    filhos: (id) => copia(dados.animais.filter((a) => a.sire_id === id || a.dam_id === id)),
    avaliacoes: (animalId) =>
      copia(
        dados.avaliacoes
          .filter((av) => !animalId || av.animal_id === animalId)
          .sort((x, y) => x.visit_date.localeCompare(y.visit_date)),
      ),
    medidas: (ids) => {
      const alvo = new Set(ids);
      return copia(dados.medidas.filter((m) => alvo.has(m.assessment_id)));
    },
    observacoes: (ids) => {
      const alvo = new Set(ids);
      return copia(dados.observacoes.filter((o) => alvo.has(o.assessment_id)));
    },
    listarVisitas: () => copia(dados.visitas),
    // Os ficticios nao tem Estacao 1 (colunas vazias na exportacao); o cavalo real tem.
    manejos: async (ids) => structuredClone(Object.fromEntries(ids.flatMap((id) => (manejoReal[id] ? [[id, manejoReal[id]]] : [])))),
    midias: (id) => copia(dados.midias.filter((m) => m.assessment_id === id)),
    midia: (id) => copia(dados.midias.find((m) => m.id === id) ?? null),
    // Ficticios: foto de exemplo, e video sem arquivo (mostra o simbolo). Cavalo real: o arquivo dele.
    urlDaMidia: async (m) => {
      const doReal = urlReal.get(m.id);
      if (doReal) return doReal;
      if (m.kind !== 'foto') return null;
      const id = dados.avaliacoes.find((av) => av.id === m.assessment_id)?.animal_id;
      const animal = id ? dados.animais.find((a) => a.id === id) : undefined;
      return animal ? fotoDeExemplo(animal.id, animal.coat_color) : null;
    },
    // Foto de perfil da ficha mais recente de cada animal (como na fonte local).
    capas: async () => {
      const ficha = new Map(dados.avaliacoes.map((av) => [av.id, av]));
      const capas = new Map<string, Midia>();
      const data = new Map<string, string>();
      for (const m of dados.midias) {
        const av = ficha.get(m.assessment_id);
        if (m.kind !== 'foto' || m.view !== 'perfil_esquerdo' || !av) continue;
        if (!capas.has(av.animal_id) || av.visit_date > (data.get(av.animal_id) ?? '')) {
          capas.set(av.animal_id, structuredClone(m));
          data.set(av.animal_id, av.visit_date);
        }
      }
      return capas;
    },
    analises: (midiaId) =>
      copia(
        todasAnalises()
          .filter((a) => a.media_id === midiaId)
          .sort((x, y) => y.created_at.localeCompare(x.created_at)),
      ),
    salvarAnalise: ({ media_id, body, supersedes }) => {
      const texto = body.trim();
      if (!podeAnalisar(usuario)) return Promise.reject(new Error('Só a equipe escreve análise.'));
      if (!texto) return Promise.reject(new Error('A análise não pode ficar vazia.'));
      // Mesma regra do unique (supersedes) no banco: uma versao so e substituida uma vez.
      if (supersedes && todasAnalises().some((a) => a.supersedes === supersedes && !a.deleted_at)) {
        return Promise.reject(new Error('Essa versão já foi substituída. Recarregue para ver a mais recente.'));
      }
      const nova: AnaliseDeMidia = {
        id: uuidv7(),
        media_id,
        author_id: usuario.id,
        author_name: usuario.nome,
        body: texto,
        supersedes,
        created_at: new Date().toISOString(),
        deleted_at: null,
      };
      salvas.push(nova);
      gravarAnalisesSalvas(salvas);
      return copia(nova);
    },
    usuarioAtual: () => copia(usuario),
    // So o cavalo real tem pontos marcados; a tela abre para ver, sem salvar.
    medicaoPorFoto: (avaliacaoId) => copia(real?.avaliacao.id === avaliacaoId ? real.medicaoPorFoto : semMedicao),
  };
}
